import { useState } from 'react';
import {
  ChevronRight,
  ChevronDown,
  DollarSign,
  TrendingUp,
} from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataLoader } from '@/components/ui/DataLoader';
import { useFeeAccruals } from '@/hooks/react-query/useFeeAccruals';
import { formatCurrency } from '@/utils/formatters';
import type {
  Granularity,
  FeeAccrualPeriod,
  FeeAccrualsResponse,
} from '@/types/fee-accruals';

interface FundFeesAccrualsProps {
  fundId: string;
  fundCurrency?: string;
}

function DetailRows({
  period,
  fundCurrency,
  isExpanded,
  onToggle,
}: {
  period: FeeAccrualPeriod;
  fundCurrency: string;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <TableRow className="cursor-pointer hover:bg-muted/50" onClick={onToggle}>
        <TableCell className="font-medium">
          <span className="inline-flex items-center gap-1">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
            {period.label}
          </span>
        </TableCell>
        <TableCell className="text-right">
          {formatCurrency(period.total, { currency: fundCurrency })}
        </TableCell>
        <TableCell className="text-right">
          {formatCurrency(period.cumulative, { currency: fundCurrency })}
        </TableCell>
      </TableRow>
      {isExpanded &&
        period.line_items.map((item) => (
          <TableRow
            key={`${period.label}-${item.category}-${item.description}`}
            className="bg-muted/30"
          >
            <TableCell className="pl-10 text-sm text-muted-foreground">
              {item.description}
              {item.detail && (
                <span className="ml-2 text-xs text-muted-foreground/70">
                  {item.detail}
                </span>
              )}
            </TableCell>
            <TableCell className="text-right text-sm text-muted-foreground">
              {formatCurrency(item.amount, { currency: fundCurrency })}
            </TableCell>
            <TableCell />
          </TableRow>
        ))}
    </>
  );
}

export function FundFeesAccruals({
  fundId,
  fundCurrency = 'EUR',
}: FundFeesAccrualsProps) {
  const [granularity, setGranularity] = useState<Granularity>('monthly');
  const [selectedYear, setSelectedYear] = useState('all');
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const query = useFeeAccruals(fundId, granularity);

  const toggleExpanded = (label: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <DataLoader query={query}>
        {(data: FeeAccrualsResponse) => {
          const years = [
            ...new Set(data.periods.map((p) => p.label.split(' ').pop()!)),
          ];
          const effectiveYear =
            selectedYear !== 'all' && years.includes(selectedYear)
              ? selectedYear
              : 'all';
          const filteredPeriods =
            effectiveYear === 'all'
              ? data.periods
              : data.periods.filter((p) => p.label.endsWith(effectiveYear));

          const currentPeriodLabel = data.periods.at(-1)?.label;

          return (
            <>
              {/* Summary */}
              <div className="grid grid-cols-2 gap-4 sm:flex sm:items-center sm:gap-8 py-2">
                <div className="flex items-center gap-3">
                  <DollarSign className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Current Period
                      {currentPeriodLabel ? ` (${currentPeriodLabel})` : ''}
                    </span>
                    <span className="text-lg font-semibold text-foreground truncate">
                      {formatCurrency(data.summary.current_period_total, {
                        currency: fundCurrency,
                      })}
                    </span>
                  </div>
                </div>
                <div className="hidden sm:block h-8 w-px bg-border flex-shrink-0" />
                <div className="flex items-center gap-3">
                  <TrendingUp className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Total Accumulated
                    </span>
                    <span className="text-lg font-semibold text-foreground truncate">
                      {formatCurrency(data.summary.total_accumulated, {
                        currency: fundCurrency,
                      })}
                    </span>
                  </div>
                </div>
              </div>

              {/* Filters */}
              <div className="flex items-center gap-4">
                <Select
                  value={granularity}
                  onValueChange={(v) => setGranularity(v as Granularity)}
                >
                  <SelectTrigger className="w-32 h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                  </SelectContent>
                </Select>

                {years.length > 1 && (
                  <Select value={effectiveYear} onValueChange={setSelectedYear}>
                    <SelectTrigger className="w-28 h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All years</SelectItem>
                      {years.map((year) => (
                        <SelectItem key={year} value={year}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Periods table */}
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Period</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">Cumulative</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredPeriods.map((period) => (
                        <DetailRows
                          key={period.label}
                          period={period}
                          fundCurrency={fundCurrency}
                          isExpanded={expanded.has(period.label)}
                          onToggle={() => toggleExpanded(period.label)}
                        />
                      ))}
                      {filteredPeriods.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={3}
                            className="text-center text-muted-foreground py-8"
                          >
                            No accrual data available
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          );
        }}
      </DataLoader>
    </div>
  );
}
